import { useEffect, useRef } from 'react';
import { useAppDispatch } from '@store';
import {
  CROSS_TAB_SYNC_KEY,
  CROSS_TAB_SYNC_META,
  getCurrentTabId,
  type CrossTabSyncData,
} from '@store/middleware/crossTabSync';
import type { AnyAction } from '@reduxjs/toolkit';

/**
 * Hook to sync Redux state across browser tabs using localStorage
 *
 * When another tab dispatches a synced action, this hook will:
 * 1. Detect the localStorage change
 * 2. Parse the sync data
 * 3. Dispatch the same action in this tab
 *
 * @example
 * ```tsx
 * function App() {
 *   useCrossTabSync();
 *   // ... rest of app
 * }
 * ```
 */
export const useCrossTabSync = () => {
  const dispatch = useAppDispatch();
  const currentTabIdRef = useRef<string>(getCurrentTabId());
  const lastSyncTimestampRef = useRef<number>(0);

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      // Only respond to changes for our sync key from other tabs
      if (e.key !== CROSS_TAB_SYNC_KEY || !e.newValue) {
        return;
      }

      try {
        const syncData: CrossTabSyncData = JSON.parse(e.newValue);

        // Ignore updates from the same tab
        if (syncData.tabId === currentTabIdRef.current) {
          return;
        }

        // Ignore if this is an older update (shouldn't happen, but safety check)
        if (syncData.timestamp <= lastSyncTimestampRef.current) {
          return;
        }

        // Update last sync timestamp
        lastSyncTimestampRef.current = syncData.timestamp;

        // Dispatch the action in this tab with meta flag to prevent re-syncing
        const action: AnyAction = {
          type: syncData.action.type,
          ...(syncData.action.payload !== undefined && {
            payload: syncData.action.payload,
          }),
          meta: {
            [CROSS_TAB_SYNC_META]: true,
          },
        };

        dispatch(action);
      } catch (error) {
        console.warn('Failed to parse cross-tab sync data:', error);
      }
    };

    // Listen for storage events from other tabs
    window.addEventListener('storage', handleStorageChange);

    // Initialize last sync timestamp from current localStorage value
    try {
      const currentData = localStorage.getItem(CROSS_TAB_SYNC_KEY);
      if (currentData) {
        const syncData: CrossTabSyncData = JSON.parse(currentData);
        lastSyncTimestampRef.current = syncData.timestamp;
      }
    } catch (error) {
      // Ignore parse errors
    }

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [dispatch]);
};
